import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import PhoneInput from "react-phone-input-2";
import classNames from "classnames";

import { userDetailSelect } from "Layout/slice/selector";
import { setUserDetails, toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { Loader } from "components/Loader";
import { SettingsVerifiedIcon } from "assets/icons";
import {
  resendVerification,
  updateAndverifyPhone,
  updatePhone,
} from "features/Account/service";
import { phoneValidationSchema } from "features/Account/v2/Components/ChangePhone/validation";
import { DarkButton, OutlineButton } from "./GeneralPanel.styles";
import { PhoneContainer } from "./PhoneVerify.styles";

const RESEND_SECONDS = 20;

// Phone number with the existing verification flow: a new (or unverified)
// number is sent a one-time code, and the number only becomes verified once
// the code is confirmed.
export const PhoneVerify = ({ onVerified }) => {
  const dispatch = useDispatch();
  const user = useSelector(userDetailSelect);

  const savedDigits = (user?.phone || "").replace(/\D/g, "");
  const [phone, setPhone] = useState(savedDigits);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    setPhone(savedDigits);
  }, [savedDigits]);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  const changed = phone !== savedDigits;
  const isVerified = Boolean(user?.phone_verified) && !changed;
  const isUnverifiedSaved =
    Boolean(user?.phone) && !user?.phone_verified && !changed;
  const isPhoneValid =
    phone.length > 0 && phoneValidationSchema.isValidSync({ phone });
  const canSendCode = isPhoneValid && !isVerified;

  const showError = (error) =>
    dispatch(toast({ type: "error", body: parseErrorMessage(error) }));

  const handleChange = (value) => {
    setPhone(value);
    setCodeSent(false);
    setCode("");
  };

  const handleSendCode = async () => {
    setIsSending(true);
    try {
      await updatePhone({ phone: `+${phone}` });
      setCodeSent(true);
      setSeconds(RESEND_SECONDS);
      dispatch(toast({ type: "info", body: "The code has been sent!" }));
    } catch (error) {
      showError(error);
    } finally {
      setIsSending(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification({ type: "sms", email: user.email });
      setSeconds(RESEND_SECONDS);
      dispatch(toast({ type: "info", body: "The code has been sent!" }));
    } catch (error) {
      showError(error);
    }
  };

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      const response = await updateAndverifyPhone({
        mfa_code: code.trim(),
        phone: `+${phone}`,
      });
      dispatch(setUserDetails({ ...user, ...response }));
      dispatch(toast({ type: "info", body: "Phone number verified" }));
      setCodeSent(false);
      setCode("");
      if (onVerified) onVerified();
    } catch (error) {
      showError(error);
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <PhoneContainer>
      <div className="phone-row">
        <PhoneInput
          country="us"
          value={phone}
          onChange={handleChange}
          enableSearch
          specialLabel=""
          placeholder="Phone"
          inputProps={{ name: "phone", "aria-label": "Phone number" }}
          inputClass={classNames({ "is-invalid": phone && !isPhoneValid })}
        />
        {isVerified && (
          <span className="status verified">
            <SettingsVerifiedIcon />
            Verified
          </span>
        )}
        {isUnverifiedSaved && (
          <span className="status unverified">Not verified</span>
        )}
        {canSendCode && !codeSent && (
          <DarkButton
            type="button"
            onClick={handleSendCode}
            disabled={isSending}
          >
            {isSending ? <Loader className="on-btn" /> : "Send code"}
          </DarkButton>
        )}
      </div>

      {codeSent && !isVerified && (
        <div className="code-row">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            placeholder="Code"
            aria-label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <DarkButton
            type="button"
            onClick={handleVerify}
            disabled={!code.trim() || isVerifying}
          >
            {isVerifying ? <Loader className="on-btn" /> : "Verify"}
          </DarkButton>
          <OutlineButton
            type="button"
            onClick={handleResend}
            disabled={seconds > 0}
          >
            {seconds > 0
              ? `Resend in ${String(seconds).padStart(2, "0")}`
              : "Resend"}
          </OutlineButton>
        </div>
      )}
    </PhoneContainer>
  );
};

PhoneVerify.propTypes = {
  onVerified: PropTypes.func,
};
